import { useEffect } from "react";
import { Outlet } from "react-router";
import biApi from "../../../../../api/biApi";
import useClientContext from "../../../../../context/useClientContext.ts";
import { GroupsContextProvider } from "../../report-lists/groups/contexts/GroupsContextProvider.tsx";
import {
  getEditUiReportUrl,
  getUiReportsUrl,
  getViewUiReportUrl,
} from "../../report-lists/reports/utilities/editReportUrl.ts";
import type { BiApiClient } from "../contexts/ApiClientProviderContext";
import { BiApiClientProvider } from "../contexts/BiApiClientProvider.tsx";
import useReportMode from "../hooks/useReportMode";
import { useAppDispatch } from "../store/hooks/useAppDispatch";
import { fieldsStateActions } from "../store/thunks/fieldStatesThunks";
import useReportGroups from "./builder/common/hooks/useReportGroups";
import ImportantDataLoadingFailed from "./common/ImportantDataLoadingFailed";

const ReportsContainer = () => {
  const { clientCode } = useClientContext();
  const { reportGroups, actions, ui } = useReportGroups({ apiClient: biApi.uiReports });
  const { mode } = useReportMode();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (mode === "view") {
      //The mode can be changed from edit to run, so we need to reset the fields state
      dispatch(fieldsStateActions.resetFieldsState());
    }
  }, [mode, dispatch]);

  if (ui.error) {
    return (
      <ImportantDataLoadingFailed
        title="Failed to load the report"
        disclaimer="There appears to have been an issue with loading the data. Kindly attempt to reload the report. Should the issue persist, please get in touch with our support team for further assistance."
      />
    );
  }

  return (
    <BiApiClientProvider<BiApiClient>
      api={{
        updateReport: biApi.uiReports.updateReport,
        addReport: biApi.uiReports.addReport,
        createDraftReport: biApi.uiReports.createDraftReport,
        getReport: biApi.uiReports.getReport,
        runTabularReport: biApi.uiReports.runTabularReport,
        buildTabularReport: biApi.uiReports.buildTabularReport,
        buildCashFlowReport: biApi.uiReports.buildCashFlowReport,
        runCashFlowReport: biApi.uiReports.runCashFlowReport,
        buildTileReport: biApi.uiReports.buildTileReport,
        buildChartReport: biApi.uiReports.buildChartReport,
        runPivotReport: biApi.uiReports.runPivotReport,
        buildPivotReport: biApi.uiReports.buildPivotReport,
        runListReport: biApi.uiReports.runListReport,
        buildListReport: biApi.uiReports.buildListReport,
        runFinancialStatementReport: biApi.uiReports.runFinancialStatementReport,
        buildFinancialStatementReport: biApi.uiReports.buildFinancialStatementReport,
        renameReport: biApi.uiReports.renameReport,
        changeReportGroup: biApi.uiReports.changeReportGroup,
        deleteReports: biApi.uiReports.deleteReports,
        getReportAuthorizationInfo: biApi.uiReports.getReportAuthorizationInfo,
        getReportHistory: biApi.uiReports.getReportHistory,
        getReports: biApi.uiReports.getReports,
        getReportConfigurationForRunning: biApi.uiReports.getReportConfigurationForRunning,
        validateName: biApi.uiReports.validateReportName,
        getReportSharing: biApi.uiReports.getReportSharing,
        updateReportSharing: biApi.uiReports.updateReportSharing,
        getEditReportUrl: getEditUiReportUrl,
        getViewReportUrl: getViewUiReportUrl,
        getReportsUrl: getUiReportsUrl,
        requestRunTabularExport: biApi.uiReports.requestRunTabularExport,
        requestBuildTabularExport: biApi.uiReports.requestBuildTabularExport,
        requestRunPivotExport: biApi.uiReports.requestRunPivotExport,
        requestBuildPivotExport: biApi.uiReports.requestBuildPivotExport,
        requestRunListExport: biApi.uiReports.requestRunListExport,
        requestBuildListExport: biApi.uiReports.requestBuildListExport,
      }}
    >
      <GroupsContextProvider groups={{ [clientCode]: reportGroups }} ui={ui} actions={actions}>
        <Outlet />
      </GroupsContextProvider>
    </BiApiClientProvider>
  );
};

export default ReportsContainer;
